import { Controller, type FieldValues, type Path } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import TextField from '@mui/material/TextField';
import { mergeAutocompleteAdornment } from '../mergeAutocompleteAdornment';
import { renderCheckableOption } from '../checkableOption';
import { showsRecommendedHint, type FormFieldBinding } from './binding';
import { recommendedWarning } from './recommendedWarning';
import { chipsToJoined, joinedToChips } from './joinedChips';
import { chipClipboardProps } from '../chipClipboard';

export { chipsToJoined, joinedToChips };

export interface MultiSelectOptions {
    /**
     * Whether a value outside the options may be typed. `false` makes it a
     * closed vocabulary — the Bodenmerkmale ExcaBook takes from its own list.
     */
    freeSolo?: boolean;
    /** How typed text narrows the options; MUI's substring match by default. */
    filterOptions?: (
        options: string[],
        state: FilterOptionsState<string>
    ) => string[];
}

/** How a field's stored value maps to the chips shown, and back. */
interface ChipCodec {
    toChips: (value: unknown) => string[];
    fromChips: (chips: string[]) => unknown;
}

/** The chips list both codecs draw; only the value adapter differs. */
function multiSelect<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly string[],
    codec: ChipCodec,
    { freeSolo = true, filterOptions }: MultiSelectOptions
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => (
                <Autocomplete
                    multiple
                    readOnly={binding.readOnly}
                    {...chipClipboardProps({
                        value: codec.toChips(field.value),
                        getLabel: (chip: string) => chip,
                        options,
                        fromText: freeSolo ? (text) => text : undefined,
                        onChange: (next) =>
                            field.onChange(codec.fromChips(next)),
                        readOnly: binding.readOnly
                    })}
                    // Ticked options, so the list says which values are in
                    // while it stays open for the next pick (see
                    // ../checkableOption).
                    disableCloseOnSelect
                    renderOption={renderCheckableOption({ ticks: true })}
                    freeSolo={freeSolo}
                    // Commit text still sitting in the input on blur — a value
                    // typed without pressing Enter must survive Save.
                    autoSelect={freeSolo}
                    options={options as string[]}
                    // Spread only when set: an explicit `undefined` would
                    // replace MUI's default filter.
                    {...(filterOptions ? { filterOptions } : {})}
                    value={codec.toChips(field.value)}
                    onChange={(_event, value) =>
                        field.onChange(codec.fromChips(value))
                    }
                    onBlur={field.onBlur}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={field.ref}
                            label={binding.intl.formatMessage({
                                id: binding.labelId(name)
                            })}
                            error={!!fieldState.error}
                            helperText={
                                fieldState.error
                                    ? binding.validationMessage(
                                          fieldState.error.message
                                      )
                                    : undefined
                            }
                            slotProps={{
                                ...params.slotProps,
                                input: {
                                    ...params.slotProps.input,
                                    endAdornment: mergeAutocompleteAdornment(
                                        params.slotProps.input.endAdornment,
                                        recommendedWarning(
                                            binding.intl,
                                            showsRecommendedHint(
                                                binding,
                                                name,
                                                field.value
                                            ),
                                            true
                                        )
                                    )
                                }
                            }}
                        />
                    )}
                />
            )}
        />
    );
}

/** Chips over a field that stores a real `string[]`. */
export function renderMultiSelectField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly string[],
    fieldOptions: MultiSelectOptions = {}
) {
    return multiSelect(
        binding,
        name,
        options,
        {
            toChips: (value) =>
                Array.isArray(value) ? (value as string[]) : [],
            fromChips: (chips) =>
                chips.map((entry) => entry.trim()).filter(Boolean)
        },
        fieldOptions
    );
}

/**
 * Chips over a field that stores them as ONE comma-joined string — the wire
 * format ExcaBook reads.
 */
export function renderJoinedMultiSelectField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly string[],
    fieldOptions: MultiSelectOptions = {}
) {
    return multiSelect(
        binding,
        name,
        options,
        {
            toChips: (value) => joinedToChips(value, options),
            fromChips: chipsToJoined
        },
        fieldOptions
    );
}
